import { AlertTriangle, Check, ChevronRight, Loader2, Minus, X } from "lucide-react"
import { useId, useState } from "react"
import { Button } from "../ui/button"
import { Table } from "../ui/table"
import { RelativeTime } from "../ui/relative-time"
import { cn } from "../../lib/utils"
import { dismissOrderSync, isSyncing, type SyncProgress } from "../../services/order-sync"
import { buildSyncSteps, syncTotals, type SyncStat, type SyncStep } from "./orders-sync-model"

export function SyncProgressPanel({ progress }: { progress: SyncProgress }) {
  const steps = buildSyncSteps(progress)
  const running = isSyncing(progress)
  const current = steps.findIndex((step) => step.status.kind === "running")
  const hasIssues = steps.some((step) => step.hasIssues)
  return (
    <section
      aria-label="Order sync progress"
      className="mb-4 rounded-xl border border-border bg-card text-[13px]"
    >
      <p role="status" className="sr-only">
        {running
          ? `Syncing orders… Step ${current + 1} of ${steps.length} · ${steps[current]?.label}`
          : `Sync finished${hasIssues ? " with issues. Open Details for results." : "."}`}
      </p>

      {running ? (
        <RunningSync steps={steps} />
      ) : (
        <FinishedSync steps={steps} finishedAt={progress.finishedAt} />
      )}
    </section>
  )
}

function RunningSync({ steps }: { steps: SyncStep[] }) {
  const current = steps.findIndex((step) => step.status.kind === "running")
  return (
    <>
      <div className="flex min-h-14 items-center gap-3 px-4 py-3">
        <Loader2
          aria-hidden="true"
          className="size-4 shrink-0 animate-spin text-info motion-reduce:animate-none"
        />

        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="font-semibold">Syncing orders…</h2>

          <span className="text-muted-foreground">
            {`Step ${current + 1} of ${steps.length} · ${steps[current]?.label}`}
          </span>
        </div>
      </div>

      <ol aria-label="Sync steps" className="grid grid-cols-5 gap-1.5 px-4 pb-4">
        {steps.map((step) => (
          <li key={step.label} aria-current={step.status.kind === "running" ? "step" : undefined}>
            <span aria-hidden="true" className={cn("block h-1 rounded-full", trackClass(step))} />

            <span className="sr-only sm:not-sr-only sm:mt-1.5 sm:block sm:truncate sm:text-xs sm:text-muted-foreground">
              {step.label}
            </span>

            <span className="sr-only">
              {` · ${step.status.kind}${step.hasIssues ? " with issues" : ""}`}
            </span>
          </li>
        ))}
      </ol>
    </>
  )
}

function trackClass(step: SyncStep): string {
  if (step.status.kind === "running") {
    return "bg-info animate-pulse motion-reduce:animate-none"
  }
  if (step.hasIssues) {
    return "bg-warning"
  }
  return step.status.kind === "done" ? "bg-success" : "bg-muted"
}

function FinishedSync({
  steps,
  finishedAt,
}: {
  steps: SyncStep[]
  finishedAt: string | undefined
}) {
  const [open, setOpen] = useState(false)
  const detailId = useId()
  const totals = syncTotals(steps)
  const hasIssues = totals.issues.length > 0
  const Icon = hasIssues ? AlertTriangle : Check
  return (
    <>
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-2 py-2 pr-1.5 pl-3.5 sm:flex sm:min-h-14 sm:flex-wrap sm:gap-x-4 sm:py-1.5 sm:pl-4">
        <Icon
          aria-hidden="true"
          className={cn("size-4 shrink-0", hasIssues ? "text-warning" : "text-success")}
        />

        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <h2 className="font-semibold">Sync finished</h2>

          {finishedAt ? (
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              <RelativeTime iso={finishedAt} />
            </span>
          ) : null}
        </div>

        <div
          className="col-span-3 row-start-2 flex min-w-0 flex-1 flex-wrap items-center gap-x-3.5 gap-y-1.5 text-muted-foreground"
          aria-label="Sync totals"
        >
          <Stats stats={totals.counts} emphasize />

          {hasIssues ? (
            <span
              aria-label="Sync issues"
              className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full bg-warning/10 px-2.5 py-1 text-warning"
            >
              <AlertTriangle aria-hidden="true" className="size-3 shrink-0" />

              <span className="flex flex-wrap gap-x-1 tabular-nums">
                {totals.issues.map((item, index) => (
                  <span key={item.label} className="whitespace-nowrap">
                    {`${index > 0 ? "· " : ""}${item.count.toLocaleString("en-GB")} ${item.label}`}
                  </span>
                ))}
              </span>
            </span>
          ) : null}
        </div>

        <div className="col-start-3 row-start-1 ml-auto flex items-center gap-1">
          <Button
            variant="secondary"
            className="h-11 px-3 text-[13px] sm:h-8"
            aria-expanded={open}
            aria-controls={detailId}
            onClick={() => setOpen(!open)}
          >
            {open ? "Hide details" : "Details"}

            <ChevronRight
              aria-hidden="true"
              className={cn(
                "size-3 transition-transform motion-reduce:transition-none",
                open && "rotate-90"
              )}
            />
          </Button>

          <Button
            variant="ghost"
            className="size-11 p-0"
            aria-label="Dismiss sync progress"
            onClick={dismissOrderSync}
          >
            <X aria-hidden="true" className="size-4" />
          </Button>
        </div>
      </div>

      <div
        id={detailId}
        hidden={!open}
        className="border-t border-border px-3.5 pb-1.5 sm:px-4 sm:pt-1 sm:pb-2"
      >
        <Table
          embedded
          className={cn(
            "overflow-visible [&_table]:w-full [&_table]:table-auto [&_table]:text-[13px]",
            "[&_thead]:absolute [&_thead]:size-px [&_thead]:overflow-hidden [&_thead]:[clip-path:inset(50%)]",
            "[&_td]:px-0 [&_td]:py-2.5 [&_td:first-child]:w-40 [&_td:first-child]:pr-4 [&_tbody_tr:first-child]:border-t-0",
            "max-sm:[&_:is(table,tbody,tr,td)]:block max-sm:[&_:is(table,tbody,tr,td)]:w-auto",
            "max-sm:[&_td:first-child]:w-auto max-sm:[&_td:first-child]:pt-3 max-sm:[&_td:first-child]:pr-0 max-sm:[&_td:first-child]:pb-1",
            "max-sm:[&_td:last-child]:pt-0 max-sm:[&_td:last-child]:pb-3"
          )}
          rows={steps}
          rowKey={(step) => step.label}
          columns={[
            {
              key: "step",
              header: "Step",
              render: (step) => (
                <span className="inline-flex items-center gap-2 font-medium">
                  <StepIcon step={step} />

                  {step.label}

                  <span className="sr-only">{step.status.kind}</span>
                </span>
              ),
            },
            { key: "results", header: "Results", render: (step) => <StepResults step={step} /> },
          ]}
        />
      </div>
    </>
  )
}

function Stats({ stats, emphasize = false }: { stats: SyncStat[]; emphasize?: boolean }) {
  return (
    <>
      {stats.map((item) => (
        <span key={item.label} className={cn("whitespace-nowrap tabular-nums", item.className)}>
          <b className={cn("font-semibold", emphasize && "text-foreground")}>
            {item.count.toLocaleString("en-GB")}
          </b>

          {` ${item.label}`}
        </span>
      ))}
    </>
  )
}

function StepIcon({ step }: { step: SyncStep }) {
  if (step.status.kind === "skipped") {
    return <Minus aria-hidden="true" className="size-4 text-muted-foreground" />
  }
  if (step.status.kind === "error") {
    return <X aria-hidden="true" className="size-4 text-destructive" />
  }
  return step.hasIssues ? (
    <AlertTriangle aria-hidden="true" className="size-4 text-warning" />
  ) : (
    <Check aria-hidden="true" className="size-4 text-success" />
  )
}

function StepResults({ step }: { step: SyncStep }) {
  return (
    <>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
        <Stats stats={step.stats} />

        {step.stats.length === 0 ? <span>{emptyResult(step)}</span> : null}
      </div>

      {step.notes.map((note, index) => (
        <p key={index} className="mt-1 text-xs text-destructive [overflow-wrap:anywhere]">
          {note}
        </p>
      ))}
    </>
  )
}

function emptyResult(step: SyncStep): string {
  if (step.status.kind === "skipped") {
    return "Skipped — a previous step failed."
  }
  if (step.status.kind === "pending") {
    return "Pending"
  }
  if (
    step.status.kind === "done" &&
    step.status.result.kind === "marketplaces" &&
    step.status.result.summary.results.length === 0
  ) {
    return "No marketplaces"
  }
  return "No changes"
}
